import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { AppState } from '../../store';
import { fetchGalleryImages, postGalleryImage } from './asyncThunks';
import {
  GalleryImagesType,
  GalleryItem,
  ImageResponseType,
  ImageType,
} from './types';

const imagesAdapter = createEntityAdapter<ImageType>({
  selectId: (image) => image.path,
});

export const imageSlice = createSlice({
  name: 'images',
  initialState: imagesAdapter.getInitialState({
    loading: true,
    error: false,
    isModalOpen: false,
    activeTab: 0,
    page: 1,
    search: '',
    images: null as ImageType[],
    galleryImages: null as GalleryImagesType,
    selectedGalleryImages: [] as GalleryItem[],
    uploadedImageInTab: null as ImageType,
    computerImages: [] as ImageType[],
    imageResponseData: null as ImageResponseType[],
    disableLoadingImages: false,
  }),
  reducers: {
    cleanAll: (state) => {
      state.selectedGalleryImages = [];
      imagesAdapter.removeAll(state);
      state.imageResponseData = null;
    },
    addImage: (state, action) => {
      imagesAdapter.addOne(state, action.payload);
    },
    addComputerImages: (state, action) => {
      state.computerImages.push(action.payload);
    },
    addBulkImage: (state, action) => {
      state.selectedGalleryImages = [];
      imagesAdapter.removeAll(state);
      imagesAdapter.addMany(state, action.payload);
    },
    updateImage: (state, action) => {
      imagesAdapter.updateOne(state, action.payload);
    },
    deleteImage: (state, action) => {
      state.imageResponseData = state.imageResponseData?.filter(
        (e) => e.image?.name !== action.payload
      );
      imagesAdapter.removeOne(state, action.payload);
    },
    toggleModal: (state, action) => {
      if (action.payload) {
        state.uploadedImageInTab = null;
      }
      state.activeTab = 0;
      state.isModalOpen = action.payload;
    },
    changeActiveTab: (state, action) => {
      state.uploadedImageInTab = null;
      state.activeTab = action.payload;
    },
    setUploadedImageInTab: (state, action) => {
      state.uploadedImageInTab = action.payload;
    },
    selectGalleryImage: (state, action) => {
      state.selectedGalleryImages.push(action.payload);
    },
    cleanGalleryImages: (state) => {
      state.selectedGalleryImages = [];
    },
    cleanComputerImages: (state) => {
      state.computerImages = [];
    },
    removeGalleryImage: (state, action) => {
      state.selectedGalleryImages = state.selectedGalleryImages.filter(
        (img) => img.image.uniqueName !== action.payload
      );
    },
    changePage: (state, action) => {
      state.page = action.payload;
    },
    changeAllFilters: (state, action) => {
      state.page = action.payload.page;
      state.search = action.payload.search;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGalleryImages.fulfilled, (state, action) => {
      state.galleryImages = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchGalleryImages.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchGalleryImages.rejected, (state) => {
      state.error = true;
    });
    builder.addCase(postGalleryImage.fulfilled, (state, action) => {
      state.imageResponseData = state.imageResponseData
        ? [...state.imageResponseData, ...action.payload]
        : [...action.payload];
      state.disableLoadingImages = false;
      state.loading = false;
    });
    builder.addCase(postGalleryImage.pending, (state) => {
      state.disableLoadingImages = true;
      state.loading = true;
    });
    builder.addCase(postGalleryImage.rejected, (state) => {
      state.error = true;
      state.disableLoadingImages = false;
      state.loading = false;
    });
  },
});

export const {
  addImage,
  addComputerImages,
  deleteImage,
  updateImage,
  toggleModal,
  changeActiveTab,
  setUploadedImageInTab,
  selectGalleryImage,
  removeGalleryImage,
  changePage,
  changeAllFilters,
  cleanGalleryImages,
  cleanComputerImages,
  cleanAll,
  addBulkImage,
} = imageSlice.actions;
export const imageSelectors = imagesAdapter.getSelectors<AppState>(
  (state) => state.images
);
imagesAdapter.getSelectors<AppState>((state) => state.images);

export const { selectAll, selectById, selectIds, selectEntities } =
  imageSelectors;

export default imageSlice.reducer;
